import { Injectable, Inject } from '@angular/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_CONFIG } from '@okta/okta-angular';
import { GlobalService } from './global.service';
import { RoleEvaluatorService } from './role-evaluator.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userInfo: any;
  selectedSystemId: string | null = null;

  constructor(
    @Inject(OKTA_CONFIG) private readonly  config: { oktaAuth: OktaAuth },
    private readonly globalVariable: GlobalService,
    private readonly roleEvaluator: RoleEvaluatorService,
    private readonly router: Router
  ) {  }

  private get oktaAuth(): OktaAuth {
    return this.config.oktaAuth;
  }

  async isAuthenticated(): Promise<boolean> {
    return this.oktaAuth.isAuthenticated();
  }

  signIn(): void {
    // Set original URI to first allowed route for dynamic post-login redirect
    const allowedRoutes = this.roleEvaluator.getAllowedRoutes();
     const requestedRoute = this.router.url.toLowerCase();
      console.log(`Requested Route: ${requestedRoute}`);
      if (allowedRoutes.length > 0) {
        this.oktaAuth.setOriginalUri(allowedRoutes[0]);
        if (!requestedRoute.startsWith('/documentviewer') && !requestedRoute.startsWith('/ddr')){ 
         if (allowedRoutes.includes(requestedRoute)) {
            this.router.navigateByUrl(requestedRoute);
            console.log(`Requested Route by navigateByUrl: ${requestedRoute}`);
          } else {
            this.router.navigate([allowedRoutes[0]]);
            console.log(`Requested Route by allowedRoutes[0]: ${allowedRoutes[0]}`);
          }
          console.log(`Requested Route inside the if condition: ${requestedRoute}`);
        }
      }
    this.oktaAuth.signInWithRedirect();
  }

  async signOut(): Promise<void> {
    await this.oktaAuth.signOut();
    this.globalVariable.clearLocalStorage();
  }

  async saveUserInfo(): Promise<void> {
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      console.warn('[AuthService] Not authenticated. Cannot save user info.');
      return;
    }

    this.handlePostLoginRedirect();

    try {
      const user = await this.oktaAuth.getUser();
      this.userInfo = user;
      localStorage.setItem('userInfo', JSON.stringify(user));

      const gpid = this.normalizeGpid(user?.['gpid']);
      if (gpid) {
        this.globalVariable.setUserGPID = gpid;
      }
      this.selectedSystemId = this.globalVariable.getSelectedSystemId;
    } catch (error) {
      console.error('[AuthService] Error getting user info:', error);
    }
  }

  private handlePostLoginRedirect(): void {
    const allowedRoutes = this.roleEvaluator.getAllowedRoutes();
    const requestedRoute = this.router.url.toLowerCase();
    console.log(`Requested Route: ${requestedRoute}`);

    if (allowedRoutes.length === 0) {
      return;
    }

    this.oktaAuth.setOriginalUri(allowedRoutes[0]);
    if (this.shouldSkipRouteRedirect(requestedRoute)) {
      return;
    }

    const targetRoute = allowedRoutes.includes(requestedRoute) ? requestedRoute : allowedRoutes[0];
    if (targetRoute === requestedRoute) {
      this.router.navigateByUrl(requestedRoute);
      console.log(`Requested Route by navigateByUrl: ${requestedRoute}`);
    } else {
      this.router.navigate([allowedRoutes[0]]);
      console.log(`Requested Route by allowedRoutes[0]: ${allowedRoutes[0]}`);
    }

    console.log(`Requested Route inside the if condtion: ${requestedRoute}`);
  }

  private shouldSkipRouteRedirect(requestedRoute: string): boolean {
    return requestedRoute.startsWith('/documentviewer')
      || requestedRoute.startsWith('/trafficcopviewer')
      || requestedRoute.startsWith('/ddr');
  }

  private normalizeGpid(gpid: unknown): string | null {
    return typeof gpid === 'string' ? gpid.replace(/"/g, '') : null;
  }

  getUserInfo(): any {
    const userInfoStr = localStorage.getItem('userInfo');
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  }

  getUserGpid(): string | null {
    return this.globalVariable.getUserGPID;
  }
}
